/**
 * LightField — a managed cloud of SoftLights. The "repeat the look anywhere"
 * primitive: hand it a region + a palette + a count and it fills the space
 * with slow-drifting blurred lights, each independently seeded (position,
 * colour, drift bearing, breathing phase). One call → the whole djmsqrvve
 * light-bed. Scenes / presets compose these; LightField owns the lifecycle.
 *
 * Placement is pure + deterministic (lightFieldConfig.placeLightField); this
 * class only instantiates SoftLights from the placements and tears them down.
 */
import type { ISprite, SpriteOptions } from '../../rendering/IRenderer';
import type { Region } from '../atmosphere/atmospherePlacement';
import { SoftLight } from './SoftLight';
import { placeLightField, type LightFieldConfig } from './lightFieldConfig';

export interface LightFieldOptions {
  /** Base seed — same seed ⇒ identical field across reloads (no flicker). */
  seed: number;
  /** Z layer for every light (defaults to ATMOSPHERE inside SoftLight). */
  depth?: number;
  /** Optional display parent (Scene FX v3 M1) — forwarded to every SoftLight so
   *  the whole field mints into one per-layer container. */
  parent?: { addChild(child: unknown): void };
  /** Injectable for tests; forwarded to each SoftLight. */
  spriteFactory?: (opts: SpriteOptions) => ISprite | null;
  /** Forwarded to each SoftLight: hold still (reduced motion). */
  still?: boolean;
}

export class LightField {
  private lights: SoftLight[] = [];
  private destroyed = false;

  constructor(cfg: LightFieldConfig, region: Region, opts: LightFieldOptions) {
    for (const placed of placeLightField(cfg, region, opts.seed)) {
      const light = new SoftLight({
        x: placed.x,
        y: placed.y,
        spec: placed.spec,
        phase: placed.phase,
        depth: opts.depth,
        parent: opts.parent,
        spriteFactory: opts.spriteFactory,
        still: opts.still,
      });
      // Drop lights whose sprite never mounted (texture race) so size() is honest.
      if (light.isAlive()) this.lights.push(light);
      else light.destroy();
    }
  }

  /** Number of live lights in the field. */
  size(): number {
    return this.lights.length;
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    for (const l of this.lights) {
      try { l.destroy(); } catch { /* already gone */ }
    }
    this.lights = [];
  }
}
